import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Box, useTheme } from '@mui/material';
import { format } from 'date-fns';
import { currencyFormatter } from 'src/util';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export interface StockValuePoint {
  date: string;
  netValue: number;
}

interface StockValueChartProps {
  data: StockValuePoint[];
  loading?: boolean;
}

const StockValueChart: React.FC<StockValueChartProps> = ({ data, loading = false }) => {
  const theme = useTheme();

  const chartData = {
    labels: data.map(point => {
      // Format dates for display
      try {
        return format(new Date(point.date), 'MMM dd');
      } catch {
        return point.date;
      }
    }),
    datasets: [
      {
        label: 'Stock Value',
        data: data.map(point => point.netValue / 100), // Convert cents to dollars
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.main + '20', // 20% opacity
        borderWidth: 3,
        fill: true,
        tension: 0.1,
        pointBackgroundColor: theme.palette.primary.main,
        pointBorderColor: theme.palette.background.paper,
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: false, // Hide legend as we only have one dataset
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: theme.palette.background.paper,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.primary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            return `Stock Value: ${currencyFormatter.format(context.parsed.y)}`;
          },
          title: function(context) {
            if (context.length > 0 && data[context[0].dataIndex]) {
              try {
                return format(new Date(data[context[0].dataIndex].date), 'MMMM dd, yyyy');
              } catch {
                return data[context[0].dataIndex].date;
              }
            }
            return '';
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          color: theme.palette.divider,
        },
        ticks: {
          color: theme.palette.text.secondary,
          maxTicksLimit: 8,
        },
      },
      y: {
        display: true,
        grid: {
          color: theme.palette.divider,
        },
        ticks: {
          color: theme.palette.text.secondary,
          callback: function(value) {
            return currencyFormatter.format(Number(value));
          },
        },
      },
    },
    elements: {
      line: {
        tension: 0.1,
      },
    },
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          height: 400, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: 'text.secondary'
        }}
      >
        Loading chart data...
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box 
        sx={{ 
          height: 400, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: 'text.secondary'
        }}
      >
        No stock value data available for the selected time range.
      </Box>
    );
  }

  return (
    <Box sx={{ height: 400, width: '100%' }}>
      <Line data={chartData} options={options} />
    </Box>
  );
};

export default StockValueChart;